import React, { useMemo, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PeopleTableSkeleton from "../../../../components/dashboardcomponents/People/PeopleTableSkeleton";
import PeopleTable, { NameCell } from "../../../../components/dashboardcomponents/People/PeopleTable";
import StatusChip from "../../../../components/dashboardcomponents/People/StatusChip";
import {
  filterCustomers,
  mergePeopleSearchParams,
} from "../../../../utils/peopleHelpers";
import {
  buildCustomerBulkBody,
  buildCustomerCreatePayload,
  buildCustomerBanPayload,
  tabToCustomerListStatus,
} from "../../../../utils/customerMappers";
import { buildCustomerDetailView } from "../../../../components/dashboardcomponents/People/Customers/CustomerDetail/customerDetailHelpers";
import useCustomerFormOptions from "../../../../hooks/useCustomerFormOptions";
import usePeoplePermissions from "../../../../hooks/usePeoplePermissions";
import { useAnalyticsGroupId } from "../../../../hooks/useAnalyticsGroupId";
import useDebounce from "../../../../hooks/useDebounce";
import { peopleTableEllipsisSx } from "../../../../components/dashboardcomponents/People/peopleFormValidation";
import CustomerBanModal from "../../../../components/dashboardcomponents/People/Customers/CustomerBanModal";
import CustomerUnbanModal from "../../../../components/dashboardcomponents/People/Customers/CustomerUnbanModal";
import CustomerDetailSidebar from "../../../../components/dashboardcomponents/People/Customers/CustomerDetailSidebar";
import CustomerAddModal from "../../../../components/dashboardcomponents/People/Customers/CustomerAddModal";
import CustomerDeleteModal from "../../../../components/dashboardcomponents/People/Customers/CustomerDeleteModal";
import CustomerToolbar from "../../../../components/dashboardcomponents/People/Customers/CustomerToolbar";
import CustomerBulkBar from "../../../../components/dashboardcomponents/People/Customers/CustomerBulkBar";
import { parseListParam } from "../../../../components/dashboardcomponents/People/Customers/customersConfig";
import { CUSTOMER_OPEN_ADD_MODAL } from "../../../../components/dashboardcomponents/People/Customers/customerEvents";
import {
  fetchCustomersList,
  fetchCustomerById,
  createCustomer,
  banCustomer as banCustomerApi,
  unbanCustomer as unbanCustomerApi,
  deleteCustomer as deleteCustomerApi,
  bulkCustomers,
  refreshCustomerTabCounts,
} from "../../../../store/features/customersApi";
import "../../TicketManagment/style.css";

function apiMessage(payload, fallback) {
  return payload?.message || fallback;
}

export default function Customers() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedGroupId } = useAnalyticsGroupId();
  const tab = searchParams.get("tab") || "all";
  const search = searchParams.get("q") || "";
  const groupParam = searchParams.get("group") || "";

  const {
    list: rows,
    listLoading,
    detail,
    detailLoading,
    mutationLoading,
    pagination,
  } = useSelector((state) => state.peopleCustomers);
  const { groupOptions, loading: optionsLoading } = useCustomerFormOptions({
    includeTeams: false,
  });
  const perms = usePeoplePermissions();

  const [groupFilters, setGroupFilters] = useState(() => parseListParam(groupParam));
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [detailCustomerId, setDetailCustomerId] = useState(null);
  const [banCustomer, setBanCustomer] = useState(null);
  const [unbanCustomer, setUnbanCustomer] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteCount, setBulkDeleteCount] = useState(0);
  const rowsPerPage = 10;

  const patchParams = (updates) => mergePeopleSearchParams(setSearchParams, updates);

  // Typing updates the input immediately but only reaches the URL (and the
  // list + tab-count requests it drives) once the query settles.
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearchInput = useDebounce(searchInput, 400);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (debouncedSearchInput === search) return;
    patchParams({ q: debouncedSearchInput || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  useLayoutEffect(() => {
    setSelected([]);
    setPage(0);
  }, [tab, search, groupParam, selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId) return;
    dispatch(
      fetchCustomersList({
        page: page + 1,
        limit: rowsPerPage,
        status: tabToCustomerListStatus(tab),
        search: search || undefined,
        group_id: selectedGroupId,
      })
    );
  }, [dispatch, page, rowsPerPage, tab, search, selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId) return;
    dispatch(refreshCustomerTabCounts(selectedGroupId, search));
  }, [dispatch, selectedGroupId, tab, search]);

  const reloadList = useCallback(() => {
    if (!selectedGroupId) return;
    dispatch(
      fetchCustomersList({
        page: page + 1,
        limit: rowsPerPage,
        status: tabToCustomerListStatus(tab),
        search: search || undefined,
        group_id: selectedGroupId,
      })
    );
  }, [dispatch, page, rowsPerPage, tab, search, selectedGroupId]);

  useEffect(() => {
    setGroupFilters(parseListParam(groupParam));
  }, [groupParam]);

  useEffect(() => {
    if (!detailCustomerId) return;
    dispatch(fetchCustomerById({ customerId: detailCustomerId }));
  }, [detailCustomerId, dispatch]);

  useEffect(() => {
    const openAdd = () => setAddModalOpen(true);
    window.addEventListener(CUSTOMER_OPEN_ADD_MODAL, openAdd);
    return () => window.removeEventListener(CUSTOMER_OPEN_ADD_MODAL, openAdd);
  }, []);

  const filteredRows = useMemo(
    () => filterCustomers(rows, { tab: "all", search: "", groupFilters }),
    [rows, groupFilters]
  );

  const sidebarCustomer = useMemo(() => {
    if (!detailCustomerId) return null;
    if (detail?.id === detailCustomerId) return buildCustomerDetailView(detail);
    const row = rows.find((r) => r.id === detailCustomerId);
    if (row?.raw) return buildCustomerDetailView(row.raw);
    return null;
  }, [detailCustomerId, detail, rows]);

  const columns = [
    {
      key: "name",
      label: "Customer",
      width: "minmax(150px, 1.2fr)",
      render: (row) => <NameCell name={row.name} avatar={row.avatar} subtitle={row.company} />,
    },
    {
      key: "contact",
      label: "Contact",
      width: "minmax(170px, 1.4fr)",
      render: (row) => (
        <Box sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography sx={{ color: "#405261", fontSize: 12, ...peopleTableEllipsisSx }}>
            {row.email || "—"}
          </Typography>
          <Typography sx={{ color: "#405261", fontSize: 12, ...peopleTableEllipsisSx }}>
            {row.phone || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      key: "group",
      label: "Group",
      width: "minmax(112px, 0.65fr)",
      render: (row) => (
        <Typography
          sx={{
            color: "#405261",
            fontSize: 14,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            width: "100%",
          }}
        >
          {row.group}
        </Typography>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "minmax(118px, 0.6fr)",
      render: (row) => <StatusChip status={row.status} />,
    },
    { key: "chats", label: "Chats", width: "minmax(60px, 0.35fr)" },
    { key: "lastActivity", label: "Last Activity", width: "minmax(108px, 0.7fr)" },
  ];

  const actionMenu = useMemo(
    () => [
      { key: "edit", label: "Edit Customer", hidden: () => !perms.customers.canEdit },
      {
        key: "ban",
        label: "Ban Customer",
        hidden: (row) => !perms.customers.canBan || row?.status === "blocked",
      },
      {
        key: "unban",
        label: "Unban Customer",
        hidden: (row) => !perms.customers.canUnban || row?.status !== "blocked",
      },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        hidden: () => !perms.customers.canDelete,
      },
    ],
    [perms]
  );

  const onAction = (type, row) => {
    if (type === "delete") {
      setDeleteTarget(row);
      setBulkDeleteCount(0);
    } else if (type === "ban") {
      setBanCustomer(row);
    } else if (type === "unban") {
      setUnbanCustomer(row);
    } else if (type === "edit") {
      setDetailCustomerId(null);
      navigate(`/people/customers/${row.id}/edit`);
    }
  };

  const handleBanConfirm = async (duration) => {
    if (!banCustomer) return;
    const body = buildCustomerBanPayload(duration);
    const result = await dispatch(
      banCustomerApi({ customerId: banCustomer.id, body })
    );
    if (banCustomerApi.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, `${banCustomer.name} has been banned`));
      setBanCustomer(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to ban customer"));
    }
  };

  const handleUnbanConfirm = async () => {
    if (!unbanCustomer) return;
    const result = await dispatch(unbanCustomerApi({ customerId: unbanCustomer.id }));
    if (unbanCustomerApi.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, `${unbanCustomer.name} has been unbanned`));
      setUnbanCustomer(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to unban customer"));
    }
  };

  const handleRowClick = (row) => {
    setDetailCustomerId(row.id);
  };

  const onBulkApply = async (values) => {
    if (!selected.length) return;

    const tasks = [];
    if (values.assignGroup) {
      tasks.push(
        bulkCustomers(
          buildCustomerBulkBody("assign_groups", selected, {
            group_ids: [values.assignGroup],
          })
        )
      );
    }
    if (values.tags?.length) {
      tasks.push(
        bulkCustomers(
          buildCustomerBulkBody("add_tags", selected, { tags: values.tags })
        )
      );
    }

    if (!tasks.length) return;

    for (const task of tasks) {
      const result = await dispatch(task);
      if (bulkCustomers.rejected.match(result)) {
        toast.error(apiMessage(result.payload, "Bulk update failed"));
        return;
      }
    }

    toast.success("Changes applied");
    setSelected([]);
    reloadList();
  };

  const onBulkDelete = () => {
    if (!selected.length) return;
    setDeleteTarget(null);
    setBulkDeleteCount(selected.length);
  };

  const handleDeleteConfirm = async () => {
    if (mutationLoading) return;
    if (bulkDeleteCount > 0) {
      const result = await dispatch(
        bulkCustomers(buildCustomerBulkBody("delete", selected))
      );
      if (bulkCustomers.fulfilled.match(result)) {
        toast.success(apiMessage(result.payload, `${bulkDeleteCount} customer(s) deleted`));
        setSelected([]);
        setBulkDeleteCount(0);
        if (detailCustomerId && selected.includes(detailCustomerId)) setDetailCustomerId(null);
        reloadList();
      } else {
        toast.error(apiMessage(result.payload, "Some customers could not be deleted"));
      }
      return;
    }
    if (!deleteTarget) return;
    const result = await dispatch(deleteCustomerApi({ customerId: deleteTarget.id }));
    if (deleteCustomerApi.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Customer deleted"));
      if (detailCustomerId === deleteTarget.id) setDetailCustomerId(null);
      setDeleteTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to delete customer"));
    }
  };

  const handleAddCustomer = async (form) => {
    const body = buildCustomerCreatePayload(form);
    const result = await dispatch(createCustomer(body));
    if (createCustomer.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Customer added"));
      setAddModalOpen(false);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to add customer"));
    }
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setBulkDeleteCount(0);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <CustomerToolbar
        searchQuery={searchInput}
        onSearchChange={setSearchInput}
      />

      <CustomerBulkBar
        count={selected.length}
        onApply={onBulkApply}
        onReset={() => setSelected([])}
        onDelete={onBulkDelete}
        showDelete={perms.customers.canDelete}
        groups={groupOptions}
        optionsLoading={optionsLoading}
      />

      <Box
        className="Ticket-container-render"
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, width: "100%" }}
      >
        {!selectedGroupId || listLoading ? (
          <PeopleTableSkeleton columns={columns} leadWithAvatar />
        ) : (
          <PeopleTable
            columns={columns}
            rows={filteredRows}
            selected={selected}
            setSelected={setSelected}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={(p) => setPage(p)}
            actionMenu={actionMenu}
            onAction={onAction}
            onRowClick={handleRowClick}
            highlightedRowId={detailCustomerId}
            entityLabel="Customers"
            totalCount={pagination?.total}
            horizontalBleed={24}
          />
        )}
      </Box>

      <CustomerBanModal
        open={Boolean(banCustomer)}
        customer={banCustomer}
        onClose={() => setBanCustomer(null)}
        onConfirm={handleBanConfirm}
      />

      <CustomerUnbanModal
        open={Boolean(unbanCustomer)}
        customer={unbanCustomer}
        onClose={() => setUnbanCustomer(null)}
        onConfirm={handleUnbanConfirm}
      />

      {detailCustomerId && (
        <CustomerDetailSidebar
          customer={sidebarCustomer}
          loading={detailLoading && detail?.id !== detailCustomerId && !sidebarCustomer}
          onClose={() => setDetailCustomerId(null)}
          onViewDetail={() => {
            setDetailCustomerId(null);
            navigate(`/people/customers/${detailCustomerId}`);
          }}
          onEdit={() => {
            setDetailCustomerId(null);
            navigate(`/people/customers/${detailCustomerId}/edit`);
          }}
          canEdit={perms.customers.canEdit}
        />
      )}

      <CustomerAddModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onConfirm={handleAddCustomer}
        groupOptions={groupOptions}
        optionsLoading={optionsLoading}
        submitting={mutationLoading}
      />

      <CustomerDeleteModal
        open={Boolean(deleteTarget || bulkDeleteCount > 0)}
        customerName={deleteTarget?.name}
        bulkCount={bulkDeleteCount}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        loading={mutationLoading}
      />
    </Box>
  );
}
